import { FaroMenuItem } from "@components/common/faro-select/faro-menu-item";
import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { FaroTextMenuButton } from "@components/common/faro-text-menu-button";
import {
  getRoleMenuItemSx,
  RoleMenuItem,
} from "@components/role/role-menu-item";
import { useChangeRoleBulkDialogContent } from "@components/table/members/bulk-actions/bulk-change-role-helper";
import { BaseCompanyProjectIdProps } from "@custom-types/base-types";
import { BaseMemberAndTeam } from "@custom-types/member-types";
import { FaroCoreApiConst, FaroCoreApiTypes } from "@stellar/core-api-client";
import {
  projectMembersSelector,
  selectedProjectManagerSelector,
} from "@store/projects/projects-selector";
import { updateMemberRoleInProjectBulk } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  bulkActionNameSelector,
  selectedEntitiesSelector,
  shouldShowSuccessDialogSelector,
} from "@store/table/table-selector";
import { setBulkActionName, updateOne } from "@store/table/table-slice";
import { currentUserSelector } from "@store/user/user-selector";
import { isProjectAdminSelfDowngrade } from "@utils/access-control/project-role-change-guard";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";
import { Text, UserRoleIcon } from "@flat-ui";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";

/** Button allowing to change role of members in project */
export function BulkChangeProjectRole({
  companyId,
  projectId,
}: BaseCompanyProjectIdProps): JSX.Element {
  const [shouldShowDialog, setShouldShowDialog] = useState<boolean>(false);
  const [newRole, setNewRole] =
    useState<FaroCoreApiTypes.ProjectRole>("RESOURCE_VIEWER");

  const { user: selectedProjectManager } = useAppSelector(
    selectedProjectManagerSelector
  );
  const currentUser = useAppSelector(currentUserSelector);
  const projectMembers = useAppSelector(projectMembersSelector);
  const bulkActionName = useAppSelector(bulkActionNameSelector);

  const dispatch = useAppDispatch();
  const shouldShowSuccessDialog = useAppSelector(
    shouldShowSuccessDialogSelector
  );
  const selectedEntities = useAppSelector(selectedEntitiesSelector("members"));
  const selectedTeams = useAppSelector(selectedEntitiesSelector("teams"));
  const mode = useBulkActionsMode();

  // TODO: https://faro01.atlassian.net/browse/ST-2840 remove this variable after remove the user group on members endpoint
  const selectedEntitiesWithoutTeams = selectedEntities.filter(
    (entities) => entities.kind === "user"
  );

  const { bulkDialogContent, successDialogContent } =
    useChangeRoleBulkDialogContent({ subjectType: "project", newRole });

  // Closes the bulk dialog when the bulk action is successful to show the success dialog
  useEffect(() => {
    if (shouldShowSuccessDialog) {
      setShouldShowDialog(false);
    }
  }, [shouldShowSuccessDialog]);

  function onNewRoleClicked(event: React.MouseEvent<HTMLLIElement>): void {
    dispatch(setBulkActionName("changeRole"));

    if (selectedProjectManager) {
      dispatch(
        updateOne({
          id: selectedProjectManager.identity,
          changes: {
            status: "not-allowed",
            message: "Cannot change the role of project owner",
          },
        })
      );
    }

    // Prevent Project Admins from downgrading themselves (unless they are Company Admins)
    const currentUserProjectMember = projectMembers.find(
      (member) => member.identity === currentUser?.identity
    );

    if (
      currentUserProjectMember &&
      isProjectAdminSelfDowngrade({
        currentUser,
        memberIdentity: currentUserProjectMember.identity,
        memberProjectRole: currentUserProjectMember.role,
      })
    ) {
      dispatch(
        updateOne({
          id: currentUserProjectMember.identity,
          changes: {
            status: "not-allowed",
            message: "You cannot change your own role as Project Admin",
          },
        })
      );
    }

    // TODO: Check whether it is possible to remove type-casting: https://faro01.atlassian.net/browse/ST-1348
    setNewRole(
      event.currentTarget.dataset.myValue as FaroCoreApiTypes.ProjectRole
    );
    setShouldShowDialog(true);
  }

  const config = getBulkActionButtonConfig(mode, "Change role in project");

  return (
    <>
      <FaroTextMenuButton
        buttonTitle={
          config.shouldShowText ? (
            <Text variant="buttonM">Change role</Text>
          ) : undefined
        }
        tooltipText={config.tooltipText}
        menuPosition={{
          anchorOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
        }}
        rippleColor={config.rippleColor}
        shouldShowEndIcon={false}
        buttonSx={() => config.sx}
        startIcon={<UserRoleIcon sx={{ width: "18px", height: "18px" }} />}
      >
        {FaroCoreApiConst.allProjectRoles.map(
          (role) =>
            role !== "RESOURCE_OWNER" && (
              <FaroMenuItem
                data-my-value={role}
                key={role}
                value={role}
                onClick={onNewRoleClicked}
                sx={getRoleMenuItemSx({ isTableCell: false })}
              >
                <RoleMenuItem role={role} />
              </FaroMenuItem>
            )
        )}
      </FaroTextMenuButton>

      {/* Checking for bulkActionName to make sure the dialog of another bulk action is not opened by mistake */}
      {bulkActionName === "changeRole" && shouldShowSuccessDialog && (
        <BulkSuccessDialog dialogContent={successDialogContent} />
      )}

      {bulkActionName === "changeRole" && shouldShowDialog && (
        <FaroBulkDialog
          dialogContent={bulkDialogContent}
          onClose={() => setShouldShowDialog(false)}
          trackingEvent={BulkActionEvents.bulkChangeRole}
          bulkActionCallback={(member: BaseMemberAndTeam) =>
            dispatch(
              updateMemberRoleInProjectBulk({
                companyId,
                projectId,
                userIdentity: member.identity,
                payload: { role: newRole },
              })
            ).unwrap()
          }
          selectedEntities={[...selectedTeams, ...selectedEntitiesWithoutTeams]}
          uniqueIdKey="identity"
          tableDataType="members"
        />
      )}
    </>
  );
}
